/**
 * Where a place is, read from what the owner pastes (spec §4 P-4.2). PURE.
 *
 * THE SIDECAR'S RULES, IN THE BROWSER: `services/ai-sidecar/src/places/point.ts` reads the same two
 * inputs, and `specs/map-point.spec.js` runs that file's vectors, so the point the owner is shown
 * is the point the server stores. Exactly two inputs: typed coordinates (Arabic digits, the Arabic
 * comma and decimal sign included), and a full Google Maps link carrying the point in `q=`,
 * `query=`, `ll=` or as `@lat,lng`. A short link is refused by name: reading one means fetching it.
 */
export const SHORT_LINK_HOSTS = ['maps.app.goo.gl', 'goo.gl'];

const GOOGLE_MAPS_HOST = /^(?:www\.|maps\.)?google\.[a-z]{2,3}(?:\.[a-z]{2})?$/;
const NUMBER = '[+-]?\\d{1,3}(?:\\.\\d+)?';
const PAIR = new RegExp(`^\\s*(${NUMBER})\\s*,\\s*(${NUMBER})\\s*$`);
const AT = new RegExp(`@(${NUMBER}),(${NUMBER})`);
const QUERY_KEYS = ['q', 'query', 'll'];
const NO_POINT = Object.freeze({ ok: false, reason: 'no_point' });

const westernDigits = value =>
  (typeof value === 'string' ? value : '')
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    .replace(/،/g, ',');

const pointOf = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude))
    return { ...NO_POINT };
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return { ok: false, reason: 'out_of_range' };
  }
  return { ok: true, point: { latitude, longitude } };
};

export const parseMapPoint = raw => {
  const text = westernDigits(raw).trim();
  const typed = PAIR.exec(text);
  if (typed) return pointOf(typed[1], typed[2]);

  let url;
  try {
    url = new URL(text);
  } catch {
    return { ...NO_POINT };
  }
  const host = url.hostname.toLowerCase();
  if (SHORT_LINK_HOSTS.includes(host))
    return { ok: false, reason: 'short_link' };
  if (url.protocol !== 'https:' && url.protocol !== 'http:')
    return { ...NO_POINT };
  if (!GOOGLE_MAPS_HOST.test(host)) return { ...NO_POINT };

  const fromQuery = QUERY_KEYS.map(key => url.searchParams.get(key))
    .map(value => (value === null ? null : PAIR.exec(westernDigits(value))))
    .find(pair => pair !== null);
  if (fromQuery) return pointOf(fromQuery[1], fromQuery[2]);
  const at = AT.exec(url.pathname);
  return at ? pointOf(at[1], at[2]) : { ...NO_POINT };
};

export const mapsUrl = point =>
  `https://www.google.com/maps/search/?api=1&query=${point.latitude},${point.longitude}`;
